import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// ── useGsapStagger ───────────────────────────────────────────────────────────
// Scroll-triggered stagger reveal for a group of cards: a light 3D tilt-up
// with a back-out overshoot, replacing manual transition-delay arithmetic
// with a real GSAP timeline. Return a ref for the container; every descendant
// matching `selector` animates in together once the container crosses
// `start`. Respects prefers-reduced-motion.
export default function useGsapStagger(selector, { y = 32, stagger = 0.08, duration = 0.75, start = 'top 85%' } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const targets = el.querySelectorAll(selector);
    if (!targets.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.set(targets, { opacity: 1, y: 0, rotateX: 0, clearProps: 'transform' });
      return;
    }

    gsap.set(targets, { opacity: 0, y, rotateX: -6, transformPerspective: 800, transformOrigin: '50% 100%' });
    const st = ScrollTrigger.create({
      trigger: el,
      start,
      once: true,
      onEnter: () => gsap.to(targets, {
        opacity: 1, y: 0, rotateX: 0,
        duration, stagger, ease: 'back.out(1.5)', overwrite: true,
      }),
    });

    return () => st.kill();
  }, [selector]); // y/stagger/duration/start are static per call site

  return ref;
}
